import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Briefcase, Link as LinkIcon, Shield, ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export default function Onboarding() {
  const { profile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [formData, setFormData] = useState({
    target_role: profile?.target_role || '',
    target_location: profile?.target_location || '',
    portfolio_url: profile?.portfolio_url || '',
    social_media_url: profile?.social_media_url || '',
    ssn: profile?.ssn || '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const nextStep = () => {
    setStep((prev) => prev + 1);
  };

  const completeOnboarding = async () => {
    setLoading(true);
    setErrorMsg('');
    
    // update the profile
    const { error } = await updateProfile({
      target_role: formData.target_role,
      target_location: formData.target_location,
      portfolio_url: formData.portfolio_url,
      social_media_url: formData.social_media_url,
      ssn: formData.ssn,
      onboarding_completed: true
    });

    setLoading(false);

    if (error) {
      setErrorMsg('Failed to save profile details. Please try again.');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-indigo-600/20">
            <Briefcase className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome to Placement Point</h1>
          <p className="text-slate-500">Let's set up your profile so we can start landing your dream job.</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8 flex justify-between items-center relative">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 rounded-full z-0">
            <motion.div 
              className="h-full bg-indigo-600 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: step === 1 ? '50%' : '100%' }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${step >= 1 ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30' : 'bg-slate-200 text-slate-500'}`}>1</div>
          <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${step >= 2 ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30' : 'bg-slate-200 text-slate-500'}`}>2</div>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
          {errorMsg && (
            <div className="bg-red-50 text-red-600 p-4 text-center text-sm border-b border-red-100">
              {errorMsg}
            </div>
          )}

          <div className="p-8 sm:p-10">
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-1">Career Goals</h2>
                  <p className="text-slate-500 text-sm">Tell us what you're aiming for.</p>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Target Role / Job Title
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        name="target_role"
                        value={formData.target_role}
                        onChange={handleChange}
                        placeholder="e.g. Business Analyst, Software Engineer"
                        className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Target Location (Optional)
                    </label>
                    <input
                      type="text"
                      name="target_location"
                      value={formData.target_location}
                      onChange={handleChange}
                      placeholder="e.g. Remote, UK, Canada"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                    />
                  </div>
                </div>

                <button
                  onClick={nextStep}
                  disabled={!formData.target_role.trim()}
                  className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-colors flex justify-center items-center gap-2 disabled:opacity-50 mt-8"
                >
                  Continue <ChevronRight className="w-5 h-5" />
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-1">Additional Details</h2>
                  <p className="text-slate-500 text-sm">Help us build a stronger application profile for you.</p>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Portfolio or Personal Websites (Optional)
                    </label>
                    <div className="relative">
                      <LinkIcon className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                      <textarea
                        name="portfolio_url"
                        value={formData.portfolio_url}
                        onChange={handleChange}
                        placeholder="https://yourportfolio.com (you can paste multiple links)"
                        rows="2"
                        className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all resize-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      LinkedIn & Other Social Media URLs (Optional)
                    </label>
                    <div className="relative">
                      <LinkIcon className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                      <textarea
                        name="social_media_url"
                        value={formData.social_media_url}
                        onChange={handleChange}
                        placeholder="https://linkedin.com/in/username&#10;https://github.com/username"
                        rows="2"
                        className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all resize-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Social Security Number (Optional)
                    </label>
                    <div className="relative">
                      <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        name="ssn"
                        value={formData.ssn}
                        onChange={handleChange}
                        placeholder="Required by some US employers"
                        className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Stored securely and only used for specific job applications that require it.</p>
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <button
                    onClick={() => setStep(1)}
                    className="px-6 py-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={completeOnboarding}
                    disabled={loading}
                    className="flex-1 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-colors flex justify-center items-center gap-2 disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Complete Setup'}
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
